import { useState, useEffect, useRef } from "react";
import {
  Modal,
  Button,
  Nav,
  Dropdown,
  Row,
  Col,
  Spinner,
  Form,
  Alert,
  Card,
} from "react-bootstrap";
import {
  ArrowLeft,
  User,
  FileText,
  Users,
  ChevronDown,
  Eye,
  Edit3,
  AlertCircle,
  Search,
  X,
} from "lucide-react";
import PlacementSummaryTab from "../placementpopup/placementtabs/PlacementSummaryTab.jsx";
import PlacementDetailsTab from "../placementpopup/placementtabs/PlacementDetailsTab.jsx";
import CommentsSection from "../../commentSection/CommentSection.jsx";
import StudentPopupView from "../../applied-jobs/StudentPopView/StudentPopView.jsx";
import { getStudentTabWiseInfo } from "../../../../services/StudentService";
import {
  getAppliedStudentsForJob,
  getDetailedPostedJob,
  getPlacementById,
} from "../../../../services/placementServices.js";
import "./PlacementPopup.css";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { updateAppliedStudent } from "../../../../services/AppliedStudentsService.js";


// ─── Tab registry ─────────────────────────────────────────────────────────────
const TabComponents = {
  summary: PlacementSummaryTab,
  details: PlacementDetailsTab,
};

const PlacementPopupView = ({
  show,
  onHide,
  placement,
  onUpdatePlacement,
  defaultActiveTab,
  defaultTab = "summary",
}) => {
  const resolvedDefaultTab = defaultActiveTab || defaultTab || "summary";

  const [activeTab, setActiveTab] = useState(resolvedDefaultTab);
  const [loadingTab, setLoadingTab] = useState(false);
  const [error, setError] = useState(null);
  const [detailedPlacement, setDetailedPlacement] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Applied students state with pagination
  const [appliedStudents, setAppliedStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loadingAppliedStudents, setLoadingAppliedStudents] = useState(false);
  const [columnFilters, setColumnFilters] = useState({
    studentName: "",
    contact: "",
    technologies: "",
    experience: "",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAppliedStudents, setTotalAppliedStudents] = useState(0);
  const [itemsPerPage] = useState(10);

  // Student popup
  const [showStudentPopup, setShowStudentPopup] = useState(false);
  const [selectedStudentForPopup, setSelectedStudentForPopup] = useState(null);

  // Placement selector state
  const [showPlacementSelector, setShowPlacementSelector] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const maxSelectableStudents = 50;




  const lastJobId = useRef(null);
  const fetchState = useRef({ details: false, appliedStudents: false });
  const isInitialMount = useRef(true);
  const isFetchingAppliedStudents = useRef(false);
  const lastFetchedPage = useRef(null);

  // ── Sync active tab when parent changes defaultActiveTab ─────────────────
  useEffect(() => {
    setActiveTab(resolvedDefaultTab);
  }, [resolvedDefaultTab]);

  // ── Consolidated Data Fetching Lifecycle ─────────────────
  useEffect(() => {
    if (!show || !placement?.id) {
      if (!show) {
        lastJobId.current = null;
        setDetailedPlacement(null);
      }
      return;
    }

    const isNewJob = lastJobId.current !== placement.id;
    if (isNewJob) {
      lastJobId.current = placement.id;
      fetchState.current = { details: false, appliedStudents: false };
      setDetailedPlacement(null);
      setAppliedStudents([]);
      setFilteredStudents([]);
      setTotalAppliedStudents(0);
      setCurrentPage(1);
      isFetchingAppliedStudents.current = false;
      lastFetchedPage.current = null;
    }

    if (!fetchState.current.details) {
      fetchDetailedJob();
    }
  }, [show, placement?.id]);

  // ── Fetch applied students when the applied-students tab becomes active or page changes ───
  useEffect(() => {
    if (activeTab === "applied-students" && placement?.id && show) {
      const shouldFetch =
        !isFetchingAppliedStudents.current &&
        (lastFetchedPage.current !== currentPage ||
          (currentPage === 1 && appliedStudents.length === 0));

      if (shouldFetch) {
        fetchAppliedStudents(currentPage);
      }
    }
  }, [show, placement?.id, activeTab, currentPage]);

  const fetchDetailedJob = async () => {
    if (!placement?.id || fetchState.current.details) return;
    fetchState.current.details = true;
    setIsLoadingData(true);
    try {
      const response = await getPlacementById(placement.id);
      if (response?.data?.length > 0) {
        setDetailedPlacement(response.data[0]);
      }
    } catch (err) {
      console.error("Failed to fetch detailed job:", err);
      setError("Failed to load placement details.");
    } finally {
      setIsLoadingData(false);
    }
  };

  const fetchAppliedStudents = async (page = 1) => {
    if (!placement?.id || (fetchState.current.appliedStudents && page === 1))
      return;
    fetchState.current.appliedStudents = true;
    if (!placement?.id) return;
    if (isFetchingAppliedStudents.current) return;

    isFetchingAppliedStudents.current = true;
    setLoadingAppliedStudents(true);
    try {
      const response = await getAppliedStudentsForJob(
        placement.id,
        page,
        itemsPerPage
      );
      const data = response?.data || [];
      const total =
        response?.total || response?.pagination?.total || data.length;
      const totalPagesCount =
        response?.pagination?.totalPages ||
        Math.ceil(total / itemsPerPage) ||
        1;

      setAppliedStudents(data);
      setFilteredStudents(data);
      setTotalAppliedStudents(total);
      setTotalPages(totalPagesCount);
      lastFetchedPage.current = page;
    } catch (err) {
      console.error("Error fetching applied students:", err);
      setError("Failed to load applied students: " + err.message);
      setAppliedStudents([]);
      setFilteredStudents([]);
      setTotalAppliedStudents(0);
      setTotalPages(1);
    } finally {
      setLoadingAppliedStudents(false);
      isFetchingAppliedStudents.current = false;
    }
  };

  // ── Search filter for applied students (client-side) ─────────────────────
  useEffect(() => {
    const hasActiveFilters = Object.values(columnFilters).some((v) => v !== "");

    if (!hasActiveFilters) {
      setFilteredStudents(appliedStudents);
    } else {
      setFilteredStudents(
        appliedStudents.filter((s) => {
          const nameMatch =
            !columnFilters.studentName ||
            s.studentName
              ?.toLowerCase()
              .includes(columnFilters.studentName.toLowerCase());

          const contactMatch =
            !columnFilters.contact ||
            s.studentEmail
              ?.toLowerCase()
              .includes(columnFilters.contact.toLowerCase()) ||
            s.studentPhoneNumber?.includes(columnFilters.contact);

          const techMatch =
            !columnFilters.technologies ||
            s.technologiesLearned
              ?.toLowerCase()
              .includes(columnFilters.technologies.toLowerCase());

          const expMatch =
            !columnFilters.experience ||
            s.workExperience
              ?.toLowerCase()
              .includes(columnFilters.experience.toLowerCase());

          return nameMatch && contactMatch && techMatch && expMatch;
        })
      );
    }
  }, [columnFilters, appliedStudents]);

  // Pagination handlers
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  

  // Selection for bulk placement update
  const handleStudentSelection = (studentId) => {
    if (
      selectedStudents.length >= maxSelectableStudents &&
      !selectedStudents.includes(studentId)
    ) {
      toast.warning(
        `Maximum ${maxSelectableStudents} students can be selected at a time.`
      );
      return;
    }
    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  useEffect(() => {
    if (!showPlacementSelector) return;
    const idsOnPage = new Set(filteredStudents.map((s) => s.id));
    setSelectedStudents((prev) => prev.filter((id) => idsOnPage.has(id)));
  }, [filteredStudents, showPlacementSelector]);

  const handleBulkMarkStatus = async (statusLabel, statusValue) => {
    if (selectedStudents.length === 0) {
      toast.warning("Please select at least one student.");
      return;
    }

    const confirm = await Swal.fire({
      title: `Mark as ${statusLabel}`,
      html: `Are you sure you want to mark these <strong>${selectedStudents.length}</strong> student(s) as ${statusLabel}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, Mark ${statusLabel}`,
      cancelButtonText: "Cancel",
      confirmButtonColor: statusValue === 'placed' ? "#2ecc71" : "#e74c3c",
      cancelButtonColor: "#3498db",
    });

    if (!confirm.isConfirmed) return;

    try {
      const updatePromises = selectedStudents.map(async (studentId) => {
        return await updateAppliedStudent(studentId, { placedStatus: statusValue });
      });
      await Promise.all(updatePromises);
      
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: `${selectedStudents.length} student(s) marked as ${statusLabel}.`,
        confirmButtonColor: "#2ecc71",
      });
      
      setSelectedStudents([]);
      setShowPlacementSelector(false);
      // fetch state resetting correctly to trigger fetch on current page
      fetchState.current.appliedStudents = false; 
      fetchAppliedStudents(currentPage); 
    } catch (error) {
      console.error(`Error marking students as ${statusLabel}:`, error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to mark students as ${statusLabel}. Please try again.`,
        confirmButtonColor: "#e74c3c",
      });
    }
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // ── Extract the real student ID from studentId field ──────────────────────
  // studentId in the applied-students API response is: [{ name, id }]
  const extractStudentId = (student) => {
    if (!student) return null;
    const sid = student.studentId;
    // Array of objects: [{ name, id }]  ← the actual case from API
    if (Array.isArray(sid) && sid.length > 0) {
      return sid[0]?.id || sid[0] || null;
    }
    // Plain object: { id, name }
    if (sid && typeof sid === "object") return sid.id || null;
    // Scalar string/number
    if (sid) return sid;
    // Last resort: the record's own id (for legacy shapes)
    return student.id || student._id || null;
  };

  // ── Map jobProfileId array from tab-wise API into appliedJobs format ──────
  const mapJobProfileIdsToAppliedJobs = (jobProfileIdArray = []) => {
    return jobProfileIdArray.map((job) => ({
      id: job.id,
      jobName: job.jobName,
      jobLocation: job.jobLocation,
      status: job.status,
      salary: job.salary,
      jobPostedDate: job.jobPostedDate,
      jobPositionStatus: job.jobPositionStatus,
      contactId: job.contactId,
      organizationId: job.organizationId?.[0]?.id || null,
      organizationName: job.organizationId?.[0]?.name || "N/A",
      department: job.department?.[0]?.id || null,
      departmentName: job.department?.[0]?.name || "N/A",
    }));
  };

  // ── Handle clicking a student row — opens StudentPopupView ────────────────
  const handleViewStudent = async (student) => {
    if (!student) return;

    // Extract the real student ID from studentId: [{ name, id }]
    const studentId = extractStudentId(student);

    if (!studentId) {
      console.error("Student ID not found in object:", student);
      return;
    }

    try {
      // Call the new tab-wise API with appliedJobs tab to get job data
      const response = await getStudentTabWiseInfo({
        tab: "appliedJobs",
        studentId,
      });

      const records = response?.data || [];
      const allJobs = [];
      records.forEach((record) => {
        const jobProfileIds = record.jobProfileId || [];
        jobProfileIds.forEach((job) => {
          if (!allJobs.find((j) => j.id === job.id)) {
            allJobs.push({
              ...job,
              organizationName: job.organizationId?.[0]?.name || "N/A",
              organizationId: job.organizationId?.[0]?.id || null,
              departmentName: job.department?.[0]?.name || "N/A",
              departmentId: job.department?.[0]?.id || null,
              cvUpload: record.cvUpload || "",
            });
          }
        });
      });

      setSelectedStudentForPopup({
        ...student,
        // Pass the resolved scalar id so StudentPopupView can build its payload correctly
        id: studentId,
        appliedJobs: allJobs,
      });
    } catch (err) {
      console.error("Failed to fetch student applied jobs:", err);
      // Fallback: open popup with whatever data we already have
      setSelectedStudentForPopup({
        ...student,
        id: studentId,
        appliedJobs: mapJobProfileIdsToAppliedJobs(student.jobProfileId || []),
      });
    }

    setShowStudentPopup(true);
  };

  const handleCloseAllModals = () => {
    setShowStudentPopup(false);
    setSelectedStudentForPopup(null);
    onHide();
  };

  const handleTabSelect = (key) => {
    setLoadingTab(true);
    setTimeout(() => {
      setActiveTab(key);
      setLoadingTab(false);
    }, 300);
  };

  // ── Tab config ────────────────────────────────────────────────────────────
  const tabs = [
    { key: "summary", label: "Summary", icon: User },
    { key: "details", label: "Details", icon: FileText },
    { key: "applied-students", label: "Applied Students", icon: Users },
  ];
  const visibleTabs = tabs.slice(0, 6);
  const hiddenTabs = tabs.slice(6);

  const TabIcon = ({ icon: Icon, active }) => (
    <Icon
      size={16}
      color={active ? "#ea580c" : "#6c757d"}
      style={{ marginRight: "6px" }}
    />
  );

  // ── Render tab content ────────────────────────────────────────────────────
  const renderTabContent = () => {
    if (loadingTab || isLoadingData) {
      return (
        <div
          className="d-flex flex-column align-items-center justify-content-center"
          style={{ height: "300px" }}
        >
          <Spinner
            animation="border"
            style={{
              color: "#f97316",
              width: "2.5rem",
              height: "2.5rem",
              borderWidth: "3px",
            }}
          />
          <p className="mt-3 text-muted">Loading...</p>
        </div>
      );
    }

    if (activeTab === "applied-students") return renderAppliedStudentsTab();

    const CurrentTabComponent = TabComponents[activeTab];
    if (!CurrentTabComponent) return null;

    const displayPlacement = detailedPlacement || placement;

    return (
      <CurrentTabComponent
        placement={displayPlacement}
        onHide={onHide}
        onUpdatePlacement={onUpdatePlacement}
        appliedStudents={appliedStudents}
        loadingAppliedStudents={loadingAppliedStudents}
        commentOn="jobprofiles"
        handleViewStudent={handleViewStudent}
        formatDate={formatDate}
      />
    );
  };

  const renderAppliedStudentsTab = () => {
    const hasFilters = Object.values(columnFilters).some((v) => v !== "");

    return (
      <div className="full-width-content">
        {loadingAppliedStudents ? (
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{ height: "300px" }}
          >
            <Spinner
              animation="border"
              style={{
                color: "#f97316",
                width: "2.5rem",
                height: "2.5rem",
                borderWidth: "3px",
              }}
            />
            <p className="mt-3 text-muted">Loading applied students...</p>
          </div>
        ) : (
          <>
            {/* Info Bar */}
            <div className="mb-2 d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3">
                <small className="text-muted">
                  Total: <strong>{totalAppliedStudents}</strong> applications
                </small>
                <Form.Check
                  type="switch"
                  id="placement-selector-toggle"
                  label="Show Placement Selector"
                  checked={showPlacementSelector}
                  onChange={(e) => {
                    setShowPlacementSelector(e.target.checked);
                    if (!e.target.checked) setSelectedStudents([]);
                  }}
                />
              </div>
              {hasFilters && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() =>
                    setColumnFilters({
                      studentName: "",
                      contact: "",
                      technologies: "",
                      experience: "",
                    })
                  }
                  className="p-0 text-decoration-none"
                  style={{ color: "#ea580c" }}
                >
                  Clear Filters
                </Button>
              )}
            </div>

            
            {/* Bulk Actions Bar for Placement Selection */}
            {selectedStudents.length > 0 && showPlacementSelector && (
              <Row className="mb-3">
                <Col md={12}>
                  <Card className="border-primary">
                    <Card.Body className="bg-primary bg-opacity-10">
                      <Card.Title className="text-primary">
                        <Users className="me-2" size={20} />
                        Bulk Placement Status
                        {selectedStudents.length === maxSelectableStudents && (
                          <span className="badge bg-danger ms-2">
                            Max Limit Reached
                          </span>
                        )}
                      </Card.Title>
                      <div className="d-flex gap-2 mt-3">
                        <Button
                          variant="success"
                          onClick={() => handleBulkMarkStatus('Placed', 'placed')}
                          className="d-flex align-items-center"
                        >
                          Mark as Placed
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => handleBulkMarkStatus('Not Placed', 'not placed')}
                          className="d-flex align-items-center"
                        >
                          Mark as Not Placed
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => setSelectedStudents([])}
                        >
                          Clear Selection
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {filteredStudents.length > 0 ? (
              <>
                <div className="table-responsive">
                  <table className="table table-hover table-striped table-bordered table-sm">
                    <thead className="table-primary">
                      <tr style={{ backgroundColor: "#f8f9fa" }}>
                        {showPlacementSelector && (
                          <th style={{ width: "40px", textAlign: "center", verticalAlign: "middle" }}>
                            <Form.Check
                              type="checkbox"
                              checked={filteredStudents.length > 0 && filteredStudents.every((s) => selectedStudents.includes(s.id))}
                              onChange={() => {
                                const allSelected = filteredStudents.length > 0 && filteredStudents.every((s) => selectedStudents.includes(s.id));
                                if (allSelected) {
                                  setSelectedStudents([]);
                                } else {
                                  const allIds = filteredStudents.map((s) => s.id);
                                  if (allIds.length > maxSelectableStudents) {
                                    setSelectedStudents(allIds.slice(0, maxSelectableStudents));
                                    toast.warning(`Selected first ${maxSelectableStudents} students due to limit.`);
                                  } else {
                                    setSelectedStudents(allIds);
                                  }
                                }
                              }}
                              style={{ cursor: "pointer" }}
                            />
                          </th>
                        )}
                        <th style={{ minWidth: "180px" }}>
                          <div className="fw-semibold mb-1">Student</div>
                          <Form.Control
                            size="sm"
                            placeholder="Search..."
                            value={columnFilters.studentName}
                            onChange={(e) =>
                              setColumnFilters((prev) => ({
                                ...prev,
                                studentName: e.target.value,
                              }))
                            }
                            style={{ fontSize: "11px", height: "24px" }}
                          />
                        </th>
                        <th style={{ minWidth: "180px" }}>
                          <div className="fw-semibold mb-1">Contact</div>
                          <Form.Control
                            size="sm"
                            placeholder="Search..."
                            value={columnFilters.contact}
                            onChange={(e) =>
                              setColumnFilters((prev) => ({
                                ...prev,
                                contact: e.target.value,
                              }))
                            }
                            style={{ fontSize: "11px", height: "24px" }}
                          />
                        </th>
                        <th style={{ minWidth: "150px" }}>
                          <div className="fw-semibold mb-1">Technologies</div>
                          <Form.Control
                            size="sm"
                            placeholder="Search..."
                            value={columnFilters.technologies}
                            onChange={(e) =>
                              setColumnFilters((prev) => ({
                                ...prev,
                                technologies: e.target.value,
                              }))
                            }
                            style={{ fontSize: "11px", height: "24px" }}
                          />
                        </th>
                        <th style={{ minWidth: "120px" }}>
                          <div className="fw-semibold mb-1">Experience</div>
                          <Form.Control
                            size="sm"
                            placeholder="Search..."
                            value={columnFilters.experience}
                            onChange={(e) =>
                              setColumnFilters((prev) => ({
                                ...prev,
                                experience: e.target.value,
                              }))
                            }
                            style={{ fontSize: "11px", height: "24px" }}
                          />
                        </th>
                        <th style={{ minWidth: "120px" }}>
                          <div className="fw-semibold mb-1">Status</div>
                        </th>
                        <th style={{ minWidth: "100px" }}>
                          <div className="fw-semibold mb-1">Applied Date</div>
                        </th>
                        <th style={{ width: "60px" }}>
                          <div className="fw-semibold mb-1">Actions</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStudents.map((student, index) => (
                        <tr key={student.id || index}>
                          {showPlacementSelector && (
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={selectedStudents.includes(student.id)}
                                onChange={() => handleStudentSelection(student.id)}
                              />
                            </td>
                          )}
                          <td>
                            <div className="d-flex align-items-center">
                              <div
                                className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-2"
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  fontSize: "12px",
                                }}
                              >
                                {student.studentName
                                  ?.substring(0, 2)
                                  ?.toUpperCase() || "ST"}
                              </div>
                              <button
                                type="button"
                                className="btn btn-link p-0 text-start"
                                onClick={() => handleViewStudent(student)}
                                style={{ textDecoration: "none" }}
                              >
                                <div className="fw-semibold text-primary">
                                  {student.studentName || "N/A"}
                                </div>
                              </button>
                            </div>
                          </td>
                          <td>
                            <div className="small">
                              {student.studentEmail || "N/A"}
                            </div>
                            <div className="small text-muted">
                              {student.studentPhoneNumber || "N/A"}
                            </div>
                          </td>
                          <td>
                            <div
                              className="small"
                              title={
                                student.technologiesLearned || "Not specified"
                              }
                            >
                              {student.technologiesLearned
                                ? student.technologiesLearned.length > 20
                                  ? student.technologiesLearned.substring(
                                      0,
                                      20
                                    ) + "..."
                                  : student.technologiesLearned
                                : "Not specified"}
                            </div>
                          </td>
                          <td>
                            <div className="small">
                              {student.workExperience || "No experience"}
                            </div>
                          </td>
                          
                          <td>
                            <div className="small">
                              {student.placedStatus === 'placed' ? (
                                <span className="badge bg-success">Placed</span>
                              ) : student.placedStatus === 'not placed' ? (
                                <span className="badge bg-danger">Not Placed</span>
                              ) : (
                                <span className="badge bg-secondary">Pending</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="small">
                              {formatDate(student.createdAt)}
                            </div>
                          </td>
                          <td>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-1"
                              onClick={() => handleViewStudent(student)}
                              title="View student details"
                            >
                              <Eye size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Component */}
                {totalPages > 1 && !hasFilters && (
                  <div className="d-flex justify-content-center align-items-center mt-4">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                      className="me-2"
                      style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
                    >
                      Previous
                    </Button>
                    <span className="text-muted small mx-3">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className="ms-2"
                      style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
                    >
                      Next
                    </Button>
                  </div>
                )}

                {/* Info when filters are active */}
                {hasFilters && filteredStudents.length > 0 && (
                  <div className="text-center mt-3">
                    <small className="text-muted">
                      Showing {filteredStudents.length} matches on current page
                    </small>
                  </div>
                )}
              </>
            ) : appliedStudents.length === 0 ? (
              <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{ height: "300px" }}
              >
                <User size={48} className="text-muted mb-3" />
                <h6 className="text-muted">No Applications Yet</h6>
                <p className="text-muted">
                  No students have applied for this job profile yet.
                </p>
              </div>
            ) : (
              <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{ height: "300px" }}
              >
                <Search size={48} className="text-muted mb-3" />
                <h6 className="text-muted">No Results Found</h6>
                <p className="text-muted">
                  No students match your search criteria.
                </p>
                <Button
                  variant="link"
                  onClick={() =>
                    setColumnFilters({
                      studentName: "",
                      contact: "",
                      technologies: "",
                      experience: "",
                    })
                  }
                  className="mt-2"
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  if (!placement) return null;

  const displayPlacement = detailedPlacement || placement;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="placement-modal"
      backdrop="static"
    >
      <div className="modal-container" style={{ padding: "20px" }}>
        <ToastContainer position="top-right" autoClose={3000} />
        <ToastContainer position="top-right" autoClose={3000} />
        <Modal.Header className="placement-modal-header" style={{ position: "relative" }}>
          <Button
            variant="link"
            onClick={onHide}
            style={{
              position: "absolute",
              top: "10px",
              right: "15px",
              zIndex: 1060,
              color: "#6c757d",
              textDecoration: "none",
              border: "none",
              background: "transparent",
              padding: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              transition: "all 0.2s ease",
            }}
            className="modal-close-x-btn"
            title="Close"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f8f9fa";
              e.currentTarget.style.color = "#212529";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#6c757d";
            }}
          >
            <X size={20} />
          </Button>
          <div className="header-content" style={{ paddingRight: "40px" }}>
            <div className="left-section">
              <Button variant="link" onClick={onHide} className="close-btn">
                <ArrowLeft size={20} />
              </Button>
              <div className="placement-info">
                <div className="placement-avatar">
                  {placement.jobName?.substring(0, 2)?.toUpperCase() || "JB"}
                </div>
                <div className="placement-details">
                  <h5 className="placement-name">{displayPlacement.jobName}</h5>
                </div>
              </div>
            </div>
            <div className="right-section">
              <div className="action-buttons">
                <Button
                  variant="link"
                  className="name-icon-btn"
                  onClick={() => handleTabSelect("details")}
                >
                  <Edit3 size={16} />
                </Button>
              </div>
            </div>
          </div>
        </Modal.Header>

        {/* Navigation Tabs */}
        <div className="tabs-container">
          <Nav variant="tabs" className="custom-tabs">
            {visibleTabs.map((tab) => (
              <Nav.Item key={tab.key}>
                <Nav.Link
                  active={activeTab === tab.key}
                  onClick={() => handleTabSelect(tab.key)}
                  className="custom-tab-link"
                >
                  <TabIcon icon={tab.icon} active={activeTab === tab.key} />
                  {tab.label}
                </Nav.Link>
              </Nav.Item>
            ))}
            {hiddenTabs.length > 0 && (
              <Nav.Item className="more-tabs-item">
                <Dropdown>
                  <Dropdown.Toggle as={Nav.Link} className="more-tabs-link">
                    <span className="tab-count">{hiddenTabs.length} More</span>
                    <ChevronDown size={12} className="more-dropdown-icon" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {hiddenTabs.map((tab) => (
                      <Dropdown.Item
                        key={tab.key}
                        onClick={() => handleTabSelect(tab.key)}
                        className={activeTab === tab.key ? "active" : ""}
                      >
                        <TabIcon
                          icon={tab.icon}
                          active={activeTab === tab.key}
                        />
                        {tab.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Nav.Item>
            )}
          </Nav>
        </div>

        <Modal.Body className="placement-modal-body">
          {error && (
            <Alert
              variant="danger"
              className="mb-4"
              dismissible
              onClose={() => setError(null)}
            >
              <AlertCircle size={16} className="me-2" />
              {error}
            </Alert>
          )}
          <div className="full-width-content">{renderTabContent()}</div>
        </Modal.Body>
      </div>

      {/* Student Popup */}
      {showStudentPopup && selectedStudentForPopup && (
        <StudentPopupView
          student={selectedStudentForPopup}
          isVisible={showStudentPopup}
          onClose={() => {
            setShowStudentPopup(false);
            setSelectedStudentForPopup(null);
          }}
          onCloseAll={handleCloseAllModals}
          onUpdate={() => {}}
          defaultActiveTab="applied-jobs"
          key={
            selectedStudentForPopup?.id ||
            selectedStudentForPopup?.studentId ||
            "student-popup"
          }
        />
      )}
      <style>{`
        .full-width-content { width: 100%; }
        .field-label { font-size: 0.85rem; color: #6c757d; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .field-value { font-size: 0.9rem; color: #212529; margin-top: 5px; font-weight: 500; }
      `}</style>
    </Modal>
  );
};

export default PlacementPopupView;